import { useSnackbar } from "notistack";
import React, { FC, useState, useMemo, useEffect } from "react";
import { useHistory, useParams } from "react-router";

import { Agent } from "@/api/generated";
import { MessageDetail } from "@/components/message/MessageDetail";
import { MessageSkeleton } from "@/components/message/MessageSkeleton";
import { MessageUnavailable } from "@/components/message/MessageUnavailable";
import {
  MESSAGE_NOT_REFUSABLE_TOAST,
  MESSAGE_REFUSE_FAILED_TOAST,
  MESSAGE_REFUSE_UNAVAILABLE_TOAST,
  MESSAGE_REFUSED_TOAST,
  RefuseConfirmModal,
} from "@/components/message/RefuseConfirmModal";
import { OfferApproveModal } from "@/components/offer/ApproveModal";
import { ProposalLoadError } from "@/components/offer/ProposalLoadError";
import { useApproveInvestmentProposal } from "@/hooks/useApproveInvestmentProposal";
import { useGetInvestmentProposalDetails } from "@/hooks/useGetInvestmentProposalDetails";
import { useRefuseInvestmentProposal } from "@/hooks/useRefuseInvestmentProposal";
import { useUpdateInvestmentRead } from "@/hooks/useUpdateInvestmentRead";
import { useUpdatePreferredContactSchedules } from "@/hooks/useUpdatePreferredContactSchedules";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { isValidProposalId } from "@/utilities/proposalId";
import { AGENT_DETAIL_URL_SOURCE } from "@/utilities/userOffer";

export const OfferInvestmentDetailPage: FC = () => {
  const { id } = useParams<{ id?: string }>();
  // 別IDでは成立通知・承認中の非同期処理を新しい画面へ引き継がない。
  return <OfferInvestmentDetailContent key={id} />;
};

const OfferInvestmentDetailContent: FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const numId = Number(id);
  const validId = isValidProposalId(numId);

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approvedAgentId, setApprovedAgentId] = useState<number | undefined>();
  const [additionalAgents, setAdditionalAgents] = useState<Agent[]>([]);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // 既読更新は副作用で一度だけ
  const updateInvestmentRead = useUpdateInvestmentRead();
  useEffect(() => {
    if (validId) updateInvestmentRead(numId);
  }, [validId, numId, updateInvestmentRead]);

  const {
    fetchInvestmentProposalById,
    proposalData,
    loading: fetching,
    notFound,
    loadError,
  } = useGetInvestmentProposalDetails(validId ? numId : undefined);

  const { approveInvestmentProposal, loading: approving } = useApproveInvestmentProposal();
  const { refuseInvestmentProposal, loading: refusing } = useRefuseInvestmentProposal();
  const { updatePreferredContactSchedules, loading: updatingPreferredContactSchedules } =
    useUpdatePreferredContactSchedules();

  const loading = useMemo(
    () => fetching || approving || updatingPreferredContactSchedules,
    [approving, fetching, updatingPreferredContactSchedules]
  );

  // 話を聞いてみるボタンの押下時
  const handleClickModalApprove = async () => {
    if (!validId) return;
    window.scrollTo(0, 0);
    try {
      const { ok, additionalAgents: recommendedAgents } = await approveInvestmentProposal(numId);
      if (!ok) return;
      // adjust向けにマッチングイベント発火
      sendEventToAppMeasurementTool(AppEventTokenEnum.ApproveProposal);

      setAdditionalAgents(recommendedAgents);
      setApprovedAgentId(proposalData?.agent?.id);
      setIsApproveModalOpen(true);
      await fetchInvestmentProposalById();
    } catch {
      // 特に何もしない
    }
  };

  // お断りの確定時。成功したらメッセージ一覧へ戻る（このプランナーのメッセージは一覧からも消える）。
  // 失敗は再試行で回復するかどうかで出し分ける（フックの RefuseInvestmentProposalResult 参照）
  const handleConfirmRefuse = async () => {
    if (!validId) return;
    const result = await refuseInvestmentProposal(numId);
    // tsconfig に strict が無く真偽値では共用体が絞り込まれないため、等値比較で判別する
    if (result.ok === true) {
      setIsRefuseModalOpen(false);
      enqueueSnackbar(MESSAGE_REFUSED_TOAST, { variant: "success" });
      history.push("/offer");
      return;
    }
    switch (result.reason) {
      case "not_found":
        // 既に表示対象外。再試行しても回復しないので、案内して一覧へ戻す
        setIsRefuseModalOpen(false);
        enqueueSnackbar(MESSAGE_REFUSE_UNAVAILABLE_TOAST, { variant: "error" });
        history.push("/offer");
        return;
      case "not_refusable":
        // マッチ済みなどでお断りできない。詳細を取り直すと CTA が現状（マッチング済み）に追従し、お断りリンクも消える
        setIsRefuseModalOpen(false);
        enqueueSnackbar(result.message || MESSAGE_NOT_REFUSABLE_TOAST, { variant: "error" });
        await fetchInvestmentProposalById();
        return;
      default:
        // 一時エラー。モーダルは開いたままにして、そのまま再試行できるようにする
        enqueueSnackbar(MESSAGE_REFUSE_FAILED_TOAST, { variant: "error" });
    }
  };

  return (
    <BaseTemplate
      isDense
      isPcV2
      headerTitle="メッセージ"
      onBack={() => (history.length > 1 ? history.goBack() : history.push("/offer"))}
      isBackGroundWhite={true}
      disableBottomPadding
    >
      {loadError && proposalData && !loading && <ProposalLoadError compact onRetry={fetchInvestmentProposalById} />}
      {loading ? (
        <MessageSkeleton detail />
      ) : loadError && !proposalData ? (
        <ProposalLoadError onRetry={fetchInvestmentProposalById} />
      ) : notFound || !proposalData ? (
        // 表示対象外・退会プランナー・削除済みなどで取得できない: 空カードではなく案内と一覧への導線を出す
        <MessageUnavailable onBackToList={() => history.push("/offer")} />
      ) : (
        <MessageDetail
          proposalData={proposalData}
          onApprove={handleClickModalApprove}
          approving={approving}
          onRefuse={() => setIsRefuseModalOpen(true)}
          refusing={refusing}
        />
      )}
      {isRefuseModalOpen && (
        <RefuseConfirmModal onConfirm={handleConfirmRefuse} onCancel={() => setIsRefuseModalOpen(false)} />
      )}
      {isApproveModalOpen && (
        <OfferApproveModal
          offerType="investment"
          agentId={approvedAgentId}
          additionalAgents={additionalAgents}
          onClickAdditionalAgent={(agent) => {
            setIsApproveModalOpen(false);
            setAdditionalAgents([]);
            history.push(`/agent_search/${agent.id}?source=${AGENT_DETAIL_URL_SOURCE.SIMILAR_AGENT}`);
          }}
          onClose={() => {
            setIsApproveModalOpen(false);
            setAdditionalAgents([]);
          }}
          onSubmitPreferredContactSchedules={async (request) => {
            if (!validId) return false;

            const ok = await updatePreferredContactSchedules("investment", numId, request);
            if (ok) {
              await fetchInvestmentProposalById();
            }
            return ok;
          }}
        />
      )}
    </BaseTemplate>
  );
};
